import { Toast } from "primereact/toast";
import AdminHeaderLinks from "../../components/adminHeaderLinks/AdminHeaderLinks.jsx";
import { Box, Grid, TextField, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { Add, Edit, Check, Close } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { createProjectRoleRequest, getProjectRolesRequest, updateProjectRoleRequest } from "../../services/wisdomServices/projectService";
import { confirmDialog } from "primereact/confirmdialog";
import { Can } from "../../security/Can.jsx";
import { PERMISSIONS } from "../../security/permissions.generated.js";

/**
 * @function AdminProjectSettingsPage
 * @returns {JSX.Element} - project settings page component
 */
function AdminProjectSettingsPage() {
    const toast = useRef(null);
    const [roles, setRoles] = useState([]);
    const [newRole, setNewRole] = useState("");
    const [editingRoleId, setEditingRoleId] = useState(null);
    const [editedRoleText, setEditedRoleText] = useState("");
    
    /**
     * @param {string} text - message to be displayed      
     */
    const showWarning = (text) => toast.current.show({severity:'warning', summary: 'Warning', detail:text, life: 3000});
    /**
     * @async
     * @function getProjectRolesAsync
     * @description - fetches all project roles from the API
     */
    const getProjectRolesAsync = async() => {
        try {
            let response = await getProjectRolesRequest()
            setRoles(response)
        } catch (error) {
            console.error(`[${new Date().toISOString()}] Something went wrong during getProjectRoles process: `, error)
        }
    }
    /**
     * @async
     * @function handleAddProjectRoleAsync
     * @description - adds a new project role after user confirmation
     */
    const handleAddProjectRoleAsync = async () => {
        if (!newRole.trim()) return showWarning("Enter a valid name for the role.");

        confirmDialog({
            message: "Are you sure you want to add this new role?",
            header: "Confirm add",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Yes",
            rejectLabel: "No",
            accept: async () => {
                try {
                    const key = {
                        role: newRole
                    };

                    const response = await createProjectRoleRequest(key);

                    const newRoleObj = {
                        id: response.id,
                        role: response.role
                    };

                    setRoles(prev => [...prev, newRoleObj]);
                    setNewRole("");
                } catch (error) {
                    console.error(`[${new Date().toISOString()}] Error in add role: `, error);
                }
            }
        });
    };
   
    /**
     * @param {number} id - role ID
     * @param {string} role - current role name
     */
    const handleEditRole = (id, role) => {
        setEditingRoleId(id);
        setEditedRoleText(role);
    };
    /**
     * @async
     * @function handleConfirmEdit
     * @param {number} id - role ID
     * @description - confirms and updates a project role
     */
    const handleConfirmEdit = async (id) => {
        if (!editedRoleText.trim()) {
            return showWarning("Enter a valid name for the role.");
        }

        confirmDialog({
            message: "Are you sure you want to update this role?",
            header: "Confirm update",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Yes",
            rejectLabel: "No",
            accept: async () => {
                try {
                    const role = {
                        role: editedRoleText
                    };

                    await updateProjectRoleRequest(id, role);

                    setRoles(prev =>
                        prev.map(r => (r.id === id ? { ...r, role: editedRoleText } : r))
                    );

                    setEditingRoleId(null);
                    setEditedRoleText("");
                } catch (error) {
                    console.error(error);
                }
            }
        });
    };

    const handleCancelEdit = () => {
        setEditingRoleId(null);
        setEditedRoleText("");
    };

    useEffect(() => {
        getProjectRolesAsync()
    }, [])

    return(
        <div className="settingsContainer">
            <AdminHeaderLinks />
            <Toast ref={toast} position="top-center" />
            <Box sx={{ flexGrow: 1 }} className="settingsBody">
                <Grid container spacing={0}>
                    <Grid item size={10}>
                        <h3 className="settingsLabel">Project Settings</h3>
                    </Grid>
                    <Grid item size={10} sx={{ display: "flex", gap: 1, mb: 2 }}>
                        <TextField
                            fullWidth
                            label="New role"
                            variant="outlined"
                            value={newRole}
                            onChange={(e) => setNewRole(e.target.value)}
                        />
                        <Can permission={PERMISSIONS.projectsRoles_post}>
                            <IconButton color="primary" onClick={handleAddProjectRoleAsync}>
                                <Add />
                            </IconButton>
                        </Can>
                    </Grid>

                    <Grid item size={10} style={{marginBottom: "20px"}}>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Role</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {roles.map(({ id, role }) => (
                                        <TableRow key={id}>
                                            <TableCell>{id}</TableCell>
                                            <TableCell>
                                                {editingRoleId === id ? (
                                                    <TextField
                                                        value={editedRoleText}
                                                        onChange={(e) => setEditedRoleText(e.target.value)}
                                                        fullWidth
                                                    />
                                                ) : (
                                                    role
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {editingRoleId === id ? (
                                                    <>
                                                        <Can permission={PERMISSIONS.projectsRolesById_put}>
                                                            <IconButton color="success" onClick={() => handleConfirmEdit(id)}>
                                                                <Check />
                                                            </IconButton>
                                                        </Can>
                                                        <IconButton color="error" onClick={handleCancelEdit}>
                                                            <Close />
                                                        </IconButton>
                                                    </>
                                                ) : (
                                                    <Can permission={PERMISSIONS.projectsRolesById_put}>
                                                        <IconButton onClick={() => handleEditRole(id, role)}>
                                                            <Edit />
                                                        </IconButton>

                                                    </Can>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </Box>
        </div>
    )
}

export default AdminProjectSettingsPage;





